import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useSearchParams, useParams } from 'react-router-dom';

/**
 * OAuth Callback Page
 * Handles OAuth redirect from Git providers (GitHub, GitLab, Bitbucket)
 * This page runs in a popup window and sends the result back to the opener
 */
const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const { provider } = useParams();
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleCallback = () => {
      // Get parameters from URL
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (error) {
        // OAuth provider returned an error
        const errorMessage = errorDescription || error;
        setError(errorMessage);
        console.error('OAuth error:', errorMessage);

        // Send error to opener window
        if (window.opener) {
          window.opener.postMessage(
            {
              type: 'oauth_callback',
              error: errorMessage
            },
            window.location.origin
          );
        }

        // Close popup after a delay
        setTimeout(() => {
          window.close();
        }, 3000);

        return;
      }

      if (code && state) {
        console.log('OAuth callback successful:', { provider, code: code.substring(0, 10) + '...', state: state.substring(0, 10) + '...' });

        // Send callback data to opener window
        if (window.opener) {
          window.opener.postMessage(
            {
              type: 'oauth_callback',
              code,
              state
            },
            window.location.origin
          );
        } else {
          console.warn('No opener window found');
        }

        // Close popup
        setTimeout(() => {
          window.close();
        }, 1000);
      } else {
        // Missing required parameters
        const errorMessage = 'OAuth callback missing required parameters (code or state)';
        setError(errorMessage);
        console.error(errorMessage);

        if (window.opener) {
          window.opener.postMessage(
            {
              type: 'oauth_callback',
              error: errorMessage
            },
            window.location.origin
          );
        }

        setTimeout(() => {
          window.close();
        }, 3000);
      }
    };

    handleCallback();
  }, [searchParams, provider]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        padding: 3
      }}
    >
      {error ? (
        <>
          <Alert severity="error" sx={{ mb: 2, maxWidth: 500 }}>
            {error}
          </Alert>
          <Typography variant="body2" color="textSecondary">
            This window will close automatically...
          </Typography>
        </>
      ) : (
        <>
          <CircularProgress size={48} />
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            Completing authentication...
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Please wait while we complete your {provider} authentication
          </Typography>
        </>
      )}
    </Box>
  );
};

export default OAuthCallback;
